import {
  KpiBase,
  KpiMetricType
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi,
  EntryType,
  QuestionSettingCode
} from '../../../data/model/questionnaire'
import { isSettingEnabled } from '../../../utils/questionnaireUtils'
import {
  getKpiMatrixRows,
  getKpiOptionLabel,
  getKpiOptions,
  getKpiRowLabel,
  getKpiSubjectKind,
  isMultipleChoiceKpiSubject
} from './kpi.utils'

export interface KpiRuleInput {
  metricType: KpiMetricType
  /** RANKED_TOP: 1 is "first". */
  topN: number
  base: KpiBase
  /** NET: the positive set. */
  optionLabels: string[]
  negativeOptionLabels: string[]
  /** Set only for a matrix row KPI; '…' when the row can't be found. */
  rowLabel?: string
  isMultipleChoice: boolean
  /** The slider opt-outs that are switched on, e.g. ['Not applicable', "Don't know"]. */
  sliderOptOuts: string[]
}

const joinWith = (names: string[], word: string) =>
  names.length > 1
    ? `${names.slice(0, -1).join(', ')} ${word} ${names[names.length - 1]}`
    : names.join('')

export const joinOr = (names: string[]): string =>
  names.length === 0 ? '…' : joinWith(names, 'or')

export const joinAnd = (names: string[]) => joinWith(names, 'and')

const quoted = (names: string[]) => names.map((name) => `"${name}"`)

const rankedMeasure = (names: string, topN: number) =>
  topN > 1
    ? `% ranking ${names} in the top ${topN}`
    : `% ranking ${names} first`

type KpiLike = Pick<
  EntryKpi,
  'metricType' | 'topN' | 'rowId' | 'optionIds' | 'negativeOptionIds'
> & { base?: KpiBase | null }

/** Labels for a KPI (or a dialog draft) as the entry currently has them; ids that no longer resolve are skipped. */
export const toKpiRuleInput = (
  kpi: KpiLike,
  entry: DraftQuestionnaireEntry
): KpiRuleInput => {
  const kind = getKpiSubjectKind(entry)
  const options = getKpiOptions(entry)
  const labelsOf = (ids: string[]) =>
    options
      .filter((option) => ids.includes(option.responseOptionLk))
      .map(getKpiOptionLabel)

  const isMatrix = entry.entryType === EntryType.MatrixEntryType
  const row = getKpiMatrixRows(entry).find(
    ({ questionLk }) => questionLk === kpi.rowId
  )
  const settingValues =
    entry.entryType === EntryType.QuestionEntryType
      ? entry.entryItem.settingValues
      : []

  return {
    metricType: kpi.metricType,
    topN: kpi.topN ?? 1,
    base: kpi.base ?? KpiBase.Answered,
    optionLabels: labelsOf(kpi.optionIds),
    negativeOptionLabels: labelsOf(kpi.negativeOptionIds ?? []),
    rowLabel: isMatrix ? (row ? getKpiRowLabel(row) : '…') : undefined,
    isMultipleChoice: !!kind && isMultipleChoiceKpiSubject(kind),
    sliderOptOuts: [
      isSettingEnabled(settingValues, QuestionSettingCode.NotApplicable) &&
        'Not applicable',
      isSettingEnabled(settingValues, QuestionSettingCode.DontKnow) &&
        "Don't know"
    ].filter((optOut): optOut is string => !!optOut)
  }
}

/**
 * One sentence per KPI. Without `questionRef` it is the card's wording ("…out of
 * everyone who answered."); with it, the dialog preview's ("…answered Q1.").
 */
export const getKpiRuleText = (
  input: KpiRuleInput,
  questionRef?: string
): string => {
  const isPreview = questionRef !== undefined
  const { metricType, topN, optionLabels, rowLabel, isMultipleChoice } = input
  const countsOnce = isMultipleChoice && optionLabels.length > 1
  const positive = joinOr(optionLabels)

  if (metricType === KpiMetricType.Average) {
    const optOuts = input.sliderOptOuts
    if (isPreview) {
      const leftOut = optOuts.length
        ? ` ${joinAnd(quoted(optOuts))} answers are left out.`
        : ''
      return `average slider answer, out of everyone who answered ${questionRef}.${leftOut}`
    }
    const leftOut = optOuts.length
      ? `; ${joinAnd(quoted(optOuts))} ${
          optOuts.length > 1 ? 'are' : 'is'
        } left out`
      : ''
    return `average of the slider answers${leftOut}.`
  }

  let measure =
    metricType === KpiMetricType.RankedTop
      ? rankedMeasure(positive, topN)
      : `% choosing ${positive}`
  if (metricType === KpiMetricType.Net) {
    measure += ` minus % choosing ${joinOr(input.negativeOptionLabels)}`
  }

  let base = 'everyone who answered'
  if (input.base === KpiBase.AllCompletes) {
    base = 'everyone who completed the survey'
  } else if (rowLabel !== undefined) {
    base += ' the row'
  } else if (isPreview) {
    base += ` ${questionRef}`
  }

  let text: string
  if (isPreview) {
    const inRow = rowLabel !== undefined ? ` in the row ${rowLabel}` : ''
    text = `${measure}${inRow}, out of ${base}.`
    if (countsOnce) {
      text += ` Someone who ticked ${
        optionLabels.length === 2 ? 'both' : 'more than one'
      } counts once.`
    }
  } else {
    const row = rowLabel !== undefined ? `row "${rowLabel}", ` : ''
    const once = countsOnce ? ', each person once' : ''
    text = `${row}${measure}${once}, out of ${base}.`
  }

  if (metricType === KpiMetricType.Net) {
    text += isPreview
      ? ' Shown in points, from −100 to +100.'
      : ' Shown in points.'
  }
  return text
}
